import axios from 'axios';
import { API, UPLOAD } from '../api/types';
import { accessDenied, apiError, apiStart, apiEnd, unauthorized, uploadStart, uploadEnd, uploadError, quotaExceeded } from '../api/actions';
import { clearSession } from '../session/actions';

// Strip an AxiosError down to a serialisable shape before it's stuffed into
// Redux state via apiError / onFailure / etc. The raw error carries
// XMLHttpRequest, the request config (with circular refs), and other
// non-serialisable bits that trip RTK's serializableStateInvariantMiddleware
// and bloat persisted state. We keep the fields any error-aware reducer or
// translator actually reads (name, code, message, status, server payload).
function sanitizeError(error) {
  if (!error || typeof error !== 'object') return error;
  return {
    name: error.name,
    code: error.code,
    message: error.message,
    response: error.response
      ? { status: error.response.status, data: error.response.data }
      : undefined,
  };
}

// onSuccess / onFailure callbacks are user-supplied. Some pages pass
// callbacks that just run side-effects (showNotification, navigate, etc.)
// and return undefined; others return plain Redux actions. Dispatching
// undefined or a non-action triggers RTK's "Actions must be plain objects"
// error and tears the request flow down. Skip silently when the callback
// didn't return a Redux action.
function dispatchIfAction(dispatch, maybeAction) {
  if (maybeAction && typeof maybeAction === 'object' && typeof maybeAction.type === 'string') {
    dispatch(maybeAction);
  }
}

const apiMiddleware =
  ({ dispatch, getState }) =>
    (next) =>
      async (action) => {
        next(action);

        if (action.type !== API && action.type !== UPLOAD) return;

        const { url, params, method, data, onSuccess, onFailure, carry, headers } = action.payload;

        // axios default configs
        axios.defaults.baseURL = process.env.REACT_API_BASE_URL || '';

        if (action.type === API) {
          axios.defaults.headers.common['Content-Type'] = 'application/json';
          let session = null;
          try {
            session = getState().session.current;
          } catch (err) {
            dispatch(clearSession());
          }

          if (session) {
            axios.defaults.headers.common.Authorization = `Bearer ${session.token}`;
            if (session.tenant) {
              axios.defaults.headers.common['X-Tenant-Id'] = session.tenant;
            } else {
              delete axios.defaults.headers.common['X-Tenant-Id'];
            }
          } else {
            delete axios.defaults.headers.common['X-Tenant-Id'];
          }
        } else {
          axios.defaults.headers.common['Content-Type'] = 'multipart/form-data';
          axios.defaults.headers.common.Authorization = ' ';
          if (getState().session.current?.tenant) {
            axios.defaults.headers.common['X-Tenant-Id'] = getState().session.current.tenant;
          } else {
            delete axios.defaults.headers.common['X-Tenant-Id'];
          }
        }

        if (carry) {
          dispatch(action.type === API ? apiStart(carry) : uploadStart(carry));
        }

        let requestHeaders = headers;
        if (headers == null) {
          requestHeaders = axios.defaults.headers.common;
        }

        axios
          .request({
            url,
            method,
            headers: requestHeaders,
            params,
            data
          })
          .then(({ data }) => {
            dispatchIfAction(dispatch, onSuccess(data));
          })
          .catch((rawError) => {
            const error = sanitizeError(rawError);
            if (error.response && error.response.status === 401) {
              dispatch(unauthorized(action.payload));
              dispatchIfAction(dispatch, onFailure(error));
            } else {
              // Handle 402 QUOTA_EXCEEDED before the generic error path so the
              // upgrade dialog is shown without a competing error toast.
              if (
                error.response
                && error.response.status === 402
                && error.response.data
                && error.response.data.error
                && error.response.data.error.code === 'QUOTA_EXCEEDED'
              ) {
                dispatch(quotaExceeded(error.response.data.error));
                dispatchIfAction(dispatch, onFailure(error));
                return;
              }

              dispatch(action.type === API ? apiError(error) : uploadError(error));
              dispatchIfAction(dispatch, onFailure(error));

              if (error.response && error.response.status === 403) {
                dispatch(accessDenied(window.location.pathname));
              }
            }
          })
          .finally(() => {
            if (carry) {
              dispatch(action.type === API ? apiEnd(carry) : uploadEnd(carry));
            }
          });
      };

export default apiMiddleware;
